import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format, endOfDay, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import { Calendar as CalendarIcon, Loader2, MapPin, Phone, User } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { LogoIcon } from '../components/Logo';
import { TurnstileChallenge, getTurnstileSiteKey } from '../components/booking/TurnstileChallenge';
import {
  createPublicCalendarAppointment,
  getPublicCalendar,
  getPublicCalendarAvailability,
  isCalendarPausedPublicError,
} from '../lib/api';

function pickMeta(payload) {
  if (!payload || typeof payload !== 'object') return {};
  return payload.calendar || payload;
}

function normalizeSlots(raw) {
  const list = raw?.slots || raw?.availability || raw?.items || (Array.isArray(raw) ? raw : []);
  if (!Array.isArray(list)) return [];
  return list.map((s) => ({
    start_at: s.start_at || s.start,
    end_at: s.end_at || s.end,
    available_spots:
      s.available_spots ?? s.free_spots ?? s.spots_remaining ?? s.remaining ?? (s.available ? 1 : 0),
  }));
}

export default function PublicBookingPage() {
  const { publicToken } = useParams();

  const [metaLoading, setMetaLoading] = useState(Boolean(publicToken));
  const [meta, setMeta] = useState(null);

  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slots, setSlots] = useState([]);

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
  });

  const turnstileSiteKey = useMemo(() => getTurnstileSiteKey(), []);
  const turnstileRequired = Boolean(turnstileSiteKey);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileMountKey, setTurnstileMountKey] = useState(0);

  const loadMeta = useCallback(async () => {
    if (!publicToken) {
      setMeta(null);
      setMetaLoading(false);
      return;
    }
    try {
      setMetaLoading(true);
      const res = await getPublicCalendar(publicToken, { suppressGlobalErrorToast: true });
      setMeta(pickMeta(res.data));
    } catch (error) {
      toast.error(error.detail || 'This booking link is invalid or expired.');
      setMeta(null);
    } finally {
      setMetaLoading(false);
    }
  }, [publicToken]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const loadDay = useCallback(async () => {
    if (!meta) return;
    const from = startOfDay(selectedDate).toISOString();
    const to = endOfDay(selectedDate).toISOString();
    const opts = { suppressGlobalErrorToast: true };

    const fetchAvailability = async () => {
      try {
        return await getPublicCalendarAvailability(publicToken, { from, to }, opts);
      } catch (e) {
        if (isCalendarPausedPublicError(e)) return { data: {} };
        throw e;
      }
    };

    try {
      setSlotsLoading(true);
      const availRes = await fetchAvailability();
      setSlots(normalizeSlots(availRes.data));
    } catch (error) {
      toast.error(error.detail || 'Could not load availability');
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [meta, publicToken, selectedDate]);

  useEffect(() => {
    if (meta) {
      loadDay();
    }
  }, [meta, loadDay]);

  const status = meta?.status || meta?.calendar_status || 'active';
  const timezone = meta?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const slotMinutes = Number(meta?.slot_duration_minutes ?? meta?.slot_duration ?? 30);

  const headline = meta?.agent_name || meta?.title || meta?.name || 'Book an appointment';

  const freeSlots = useMemo(
    () => slots.filter((s) => Number(s.available_spots) > 0 && s.start_at && s.end_at),
    [slots]
  );

  /** Backend emits only candidate bookable windows; [] means none were generated for this range—not “everything free”. */
  const hasCandidateSlots = slots.length > 0;
  const allOfferedSlotsFull = hasCandidateSlots && freeSlots.length === 0;

  const handleBook = async () => {
    if (!selectedSlot) {
      toast.error('Choose a time slot first');
      return;
    }
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error('Name, phone, and address are required');
      return;
    }
    if (turnstileRequired && !turnstileToken) {
      toast.error('Please complete the security check below.');
      return;
    }
    try {
      setSubmitting(true);
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

      const res = await createPublicCalendarAppointment(
        publicToken,
        {
          start_at: selectedSlot.start_at,
          end_at: selectedSlot.end_at,
          customer: {
            name: form.name.trim(),
            phone: form.phone.trim(),
            email: form.email.trim() || undefined,
            address: form.address.trim(),
          },
          ...(turnstileToken ? { 'cf-turnstile-response': turnstileToken } : {}),
        },
        { suppressGlobalErrorToast: true, headers: { 'Idempotency-Key': idempotencyKey } }
      );
      const reused = res.data?.reused === true;
      toast.success(reused ? 'Booking confirmed (already submitted).' : 'Your appointment is booked');
      setForm({ name: '', phone: '', email: '', address: '' });
      setSelectedSlot(null);
      setTurnstileToken(null);
      setTurnstileMountKey((k) => k + 1);
      loadDay();
    } catch (error) {
      if (isCalendarPausedPublicError(error)) {
        toast.error('Booking is paused for this calendar.');
        return;
      }
      toast.error(error.detail || 'Booking failed. That slot may have been taken.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!publicToken) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <p className="text-sm text-slate-600">Missing booking link.</p>
      </div>
    );
  }

  if (metaLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600 mb-4" />
        <p className="text-sm text-slate-600">Loading calendar…</p>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4">
        <LogoIcon className="w-12 h-12 mb-4" />
        <h1 className="text-lg font-semibold text-slate-900">Booking unavailable</h1>
        <p className="text-sm text-slate-600 mt-2 text-center max-w-md">
          We could not load this scheduling link. Ask the business for an updated link.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="public-booking-page">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="mx-auto max-w-5xl px-4 py-4 flex items-center gap-3">
          <LogoIcon className="w-10 h-10" />
          <div>
            <p className="text-xs uppercase tracking-wide text-teal-700 font-medium">Scheduling</p>
            <h1 className="text-xl font-jakarta font-bold text-slate-900">{headline}</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 space-y-8">
        {status === 'paused' && (
          <Alert variant="destructive">
            <AlertTitle>Booking paused</AlertTitle>
            <AlertDescription>
              This calendar is not accepting new appointments right now. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,320px)_1fr]">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-teal-600" />
                Choose a day
              </CardTitle>
              <CardDescription>
                Times shown in your browser. Calendar timezone:{' '}
                <span className="font-medium text-slate-700">{timezone}</span> · ~{slotMinutes} min slots
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => {
                  if (d) {
                    setSelectedDate(d);
                    setSelectedSlot(null);
                  }
                }}
                className="rounded-md border"
              />
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Availability on {format(selectedDate, 'MMMM d, yyyy')}</CardTitle>
                <CardDescription>
                  Bookable times come from working hours and slot rules. An empty list means no windows were
                  generated for this day—not wide-open availability.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {slotsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading slots…
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-sm font-medium text-slate-800 mb-2">Open slots</p>
                      {status === 'paused' ? (
                        <p className="text-sm text-slate-500">Booking is paused.</p>
                      ) : !hasCandidateSlots ? (
                        <p className="text-sm text-slate-600">
                          No bookable time windows for this date. The schedule may have no working hours on this
                          day, the day may be closed, or settings may not allow slots to be built—this does{' '}
                          <span className="font-medium">not</span> mean the whole day is open.
                        </p>
                      ) : allOfferedSlotsFull ? (
                        <p className="text-sm text-slate-600">
                          Every offered time on this day is fully booked. Try another date.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {freeSlots.map((slot) => {
                            const active =
                              selectedSlot &&
                              selectedSlot.start_at === slot.start_at &&
                              selectedSlot.end_at === slot.end_at;
                            return (
                              <Button
                                key={`${slot.start_at}-${slot.end_at}`}
                                type="button"
                                size="sm"
                                variant={active ? 'default' : 'outline'}
                                className={active ? 'bg-teal-600 hover:bg-teal-700' : ''}
                                onClick={() => setSelectedSlot(slot)}
                              >
                                {format(new Date(slot.start_at), 'p')} – {format(new Date(slot.end_at), 'p')}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Your details</CardTitle>
                <CardDescription>Required to confirm the appointment.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="pb-name">Full name</Label>
                    <div className="relative">
                      <User className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        id="pb-name"
                        className="pl-9"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        autoComplete="name"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pb-phone">Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                      <Input
                        id="pb-phone"
                        className="pl-9"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                        autoComplete="tel"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pb-email">Email (optional)</Label>
                  <Input
                    id="pb-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pb-address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                    <Textarea
                      id="pb-address"
                      className="pl-9 min-h-[80px]"
                      value={form.address}
                      onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    />
                  </div>
                </div>
                {turnstileRequired && (
                  <div className="space-y-2">
                    <Label className="text-slate-700">Security check</Label>
                    <TurnstileChallenge
                      key={turnstileMountKey}
                      siteKey={turnstileSiteKey}
                      onVerify={setTurnstileToken}
                      onExpire={() => setTurnstileToken(null)}
                    />
                  </div>
                )}
                <Button
                  type="button"
                  className="w-full sm:w-auto bg-teal-600 hover:bg-teal-700"
                  disabled={
                    submitting ||
                    status === 'paused' ||
                    !selectedSlot ||
                    (turnstileRequired && !turnstileToken)
                  }
                  onClick={handleBook}
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Request appointment
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
